import React, { memo, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import classNames from 'classnames';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import Cookies from 'js-cookie';
import spinner from '../../assets/icons/spinner.svg';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import * as Yup from 'yup';
import { QuickQuoteContext, Service } from '../../contexts/QuickQuote.context';
import { Blok } from '../../interfaces/Blok.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { requestQuotation } from '../../services/quote.service';
import { setIsLoading } from '../../store/slices/loading.store';
import { getQueryParam } from '../../utils/getQueryParam/getQueryParam.util';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { APIErrorResponse } from '../QuoteForm/QuoteAPIErrors';
import ProgressBar from './ProgressBar';
import QuickQuoteCardBtn from './QuickQuoteCardBtn';
import QuickQuoteCTA from './QuickQuoteCTA';
import { InitialValueProps } from './QuickQuoteFormProps';
import { getValidationSchema } from './QuickQuoteFormValidation';
import QuickQuoteInput from './QuickQuoteInput';
import QuickQuotePreviousCTA from './QuickQuotePreviousCTA';
import QuickQuoteWrapper from './QuickQuoteWrapper';
import Heading, { HeadingTypes } from '../Heading/Heading';
import { pushToDataLayer } from '../../utils/pushToDataLayer/pushToDataLayer.util';
import { getDataLayerQuoteFunnerJourney } from '../../utils/getDataLayerQuoteFunnelJourney/getDataLayerQuoteFunnelJourney.util';
import { ProductType } from '../../services/quote.interfaces';
import { log } from 'console';
import QuickQuoteSingleOptionSelect from './QuickQuoteSingleOptionSelect';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import QuickQuoteMultiSelectCardBtn from './QuickQuoteMultiSelectCardBtn';

export interface QuickQuoteBlokProps extends Blok {
  title: string;
  return_cta_label: string;
  return_to_service_step_cta_label: string;

  service_heading: string;
  buy_field_name: string;
  buy_field_description: string;
  buy_field_icon: string;
  sell_field_name: string;
  sell_field_description: string;
  sell_field_icon: string;
  buy_and_sell_field_name: string;
  buy_and_sell_field_description: string;
  buy_and_sell_field_icon: string;
  remortgage_field_name: string;
  remortgage_field_description: string;
  remortgage_field_icon: string;

  type_question_heading: string;
  freehold_field_name: string;
  freehold_field_description: string;
  freehold_field_icon: string;
  leasehold_field_name: string;
  leasehold_field_description: string;
  leasehold_field_icon: string;
  price_question_heading: string;
  postcode_question_heading: string;

  title_field_heading: string;
  first_name_field_heading: string;
  last_name_field_heading: string;
  email_field_heading: string;
  telephone_field_heading: string;
  personal_info_section_heading: string;
  get_quote_cta_label: string;

  im_buying_with_a_mortgage_label: string;
  im_buying_with_a_mortgage_icon: StoryblokAsset;
  new_build_property_label: string;
  new_build_property_icon: StoryblokAsset;
  this_is_a_second_or_investment_property_label: string;
  this_is_a_second_or_investment_property_icon: StoryblokAsset;
  im_a_first_time_buyer_label: string;
  im_a_first_time_buyer_icon: StoryblokAsset;
  i_already_have_a_mortgage_on_this_property_label: string;
  i_already_have_a_mortgage_on_this_property_icon: StoryblokAsset;
}
interface QuickQuoteProps {
  blok: QuickQuoteBlokProps;
}

const QuickQuote: React.FC<QuickQuoteProps> = ({ blok }) => {
  const {
    title,
    return_cta_label,
    return_to_service_step_cta_label,
    service_heading,
    buy_field_name,
    buy_field_description,
    buy_field_icon,
    sell_field_name,
    sell_field_description,
    sell_field_icon,
    buy_and_sell_field_name,
    buy_and_sell_field_description,
    buy_and_sell_field_icon,
    remortgage_field_name,
    remortgage_field_description,
    remortgage_field_icon,

    type_question_heading,
    freehold_field_name,
    freehold_field_description,
    freehold_field_icon,
    leasehold_field_name,
    leasehold_field_description,
    leasehold_field_icon,
    price_question_heading,
    postcode_question_heading,

    title_field_heading,
    first_name_field_heading,
    last_name_field_heading,
    email_field_heading,
    telephone_field_heading,
    personal_info_section_heading,
    get_quote_cta_label,

    im_buying_with_a_mortgage_label,
    im_buying_with_a_mortgage_icon,
    new_build_property_label,
    new_build_property_icon,
    this_is_a_second_or_investment_property_label,
    this_is_a_second_or_investment_property_icon,
    im_a_first_time_buyer_label,
    im_a_first_time_buyer_icon,
    i_already_have_a_mortgage_on_this_property_label,
    i_already_have_a_mortgage_on_this_property_icon,
  } = blok;

  const [quoteStep, setQuoteStep] = React.useState(0);
  const [service, setService] = React.useState<Service | null>(null);
  const location = useLocation();
  const [apiErrors, setApiErrors] = useState<APIErrorResponse[]>([]);
  const [productType, setProductType] = useState<string>('buying');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const serviceType = searchParams.get('type') as Service | null;
    if (serviceType) {
      setService(serviceType);
      setQuoteStep(1);
    }
  }, [location]);

  useEffect(() => {
    if (service) {
      setQuoteStep(1);
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.set('type', service);
      sessionStorage.setItem('scrollPosition', window.scrollY.toString());
      navigate(`${location.pathname}?${newSearchParams.toString()}`, {
        replace: true, // Use true to avoid adding entries to the history stack
      });
    }
  }, [service]);

  [
    {
      MemberNames: ['PropertyInformation'],
      'Error Message': 'Validation for PropertyInformation failed:',
      'Validation Results': [
        {
          MemberNames: ['BuyAndSellInformation'],
          'Error Message': 'Validation for BuyAndSellInformation failed:',
          'Validation Results': [
            {
              MemberNames: ['IsBuyingNewBuild'],
              'Error Message': 'The IsBuyingNewBuild field is required.',
            },
            {
              MemberNames: ['IsBuyingSecondOrInvestmentProperty'],
              'Error Message':
                'The IsBuyingSecondOrInvestmentProperty field is required.',
            },
            {
              MemberNames: ['HasMortgageOnSellProperty'],
              'Error Message':
                'The HasMortgageOnSellProperty field is required.',
            },
          ],
        },
      ],
    },
  ];
  const getRelevantFields = (service: Service | null): string[] => {
    switch (service) {
      case Service.REMORTGAGE:
        return [
          'conveyancy-choice',
          'email',
          'firstName',
          'lastName',
          'price',
          'remortgagePostcode',
          'telephone',
          'tenure',
          'title',
        ];
      case Service.BUY_AND_SELL:
        return [
          'conveyancy-choice',
          'buyPostcode',
          'buyPrice',
          'buyTenure',
          'email',
          'firstName',
          'lastName',
          'sellPostcode',
          'sellPrice',
          'sellTenure',
          'telephone',
          'title',
          'isBuyingWithMortgage',
          'isNewBuild',
          'isSecondOrInvestmentProperty',
          'hasMortgageOnProperty',
        ];
      case Service.SELL:
        return [
          'conveyancy-choice',
          'email',
          'firstName',
          'lastName',
          'telephone',
          'title',
          'hasMortgageOnProperty',
        ];
      default:
        return [
          'conveyancy-choice',
          'email',
          'firstName',
          'lastName',
          'postcode',
          'price',
          'telephone',
          'tenure',
          'title',
          'isBuyingWithMortgage',
          'isNewBuild',
          'isSecondOrInvestmentProperty',
          'isFirstTimeBuyer',
        ];
    }
  };

  const getPropertyInformation = (values: any) => {
    switch (values['conveyancy-choice']) {
      case Service.BUY:
        return {
          buyInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.postcode,
            isBuyingWithMortgage: values.isBuyingWithMortgage,
            isNewBuild: values.isNewBuild,
            isSecondOrInvestmentProperty: values.isSecondOrInvestmentProperty,
            isFirstTimeBuyer: values.isFirstTimeBuyer,
          },
        };
      case Service.SELL:
        return {
          sellInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.postcode,
            hasMortgageOnProperty: values.hasMortgageOnProperty,
          },
        };
      case Service.BUY_AND_SELL:
        return {
          buyAndSellInformation: {
            buyPostcode: values.buyPostcode,
            buyPrice: parseInt(values.buyPrice.replace(/,/g, ''), 10),
            buyTenure: values.buyTenure,

            sellPostcode: values.sellPostcode,
            sellPrice: parseInt(values.sellPrice.replace(/,/g, ''), 10),
            sellTenure: values.sellTenure,

            isBuyingWithMortgage: values.isBuyingWithMortgage,
            IsBuyingNewBuild: values.isNewBuild,
            IsBuyingSecondOrInvestmentProperty:
              values.isSecondOrInvestmentProperty,
            HasMortgageOnSellProperty: values.hasMortgageOnProperty,
          },
        };
      case Service.REMORTGAGE:
        return {
          remortgageInformation: {
            tenure: values.tenure,
            price: parseInt(values.price.replace(/,/g, ''), 10),
            postcode: values.remortgagePostcode,
          },
        };
      default: {
        return {};
      }
    }
  };

  const initialValues: InitialValueProps = {
    'conveyancy-choice': '',
    buyPostcode: '',
    buyPrice: '',
    buyTenure: '',
    email: '',
    firstName: '',
    lastName: '',
    postcode: '',
    price: '',
    remortgagePostcode: '',
    sellPostcode: '',
    sellPrice: '',
    sellTenure: '',
    telephone: '',
    tenure: '',
    title: '',
    isBuyingWithMortgage: false,
    isNewBuild: false,
    isSecondOrInvestmentProperty: false,
    isFirstTimeBuyer: false,
    hasMortgageOnProperty: false,
  };

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);

    const requestQuotationPayload = {
      customerDetails: {
        title: values.title,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        telephone: values.telephone,
      },
      leadSource: {
        sourceId:
          getQueryParam('lead_source') ||
          Cookies.get('lead_source_reference') ||
          '',
        previousQuotationReference:
          Cookies.get('previousQuotationReference') || '',
      },
    };

    const propertyInformation = getPropertyInformation(values);

    if (!executeRecaptcha) {
      console.log('Not available to execute recaptcha');
      return;
    }

    try {
      const gRecaptchaToken = await executeRecaptcha?.('quoteStageOneSubmit');

      const result = await requestQuotation({
        ...requestQuotationPayload,
        propertyInformation,
        ...(gRecaptchaToken && { gRecaptchaToken }),
      });

      setApiErrors([]);
      setIsLoading(false);

      if (result.data?.quoteReference && result.data?.trackingRef) {
        const trackingRef =
          result.data.trackingRef || result.data.trackingReference002;
        pushToDataLayer({
          event: 'quote_step1_get_quote',
          data: {
            funnel_journey: getDataLayerQuoteFunnerJourney(
              service as ProductType
            ),
            form_type: 'embedded',
          },
        });
        navigate(
          `/instant-quote/quote-stage-two/?quotation=${result.data.quoteReference}&TrackingRef=${trackingRef}`
        );
      }
    } catch (error: any) {
      setIsLoading(false);
      setApiErrors(error);
      setIsSubmitting(false);

      if (error.statusCode === 403) {
        navigate(`/instant-quote/quote-not-found/`);
      }
    }
  };

  const ValidationSchema = getValidationSchema(
    getRelevantFields(service)
  ) as Yup.ObjectSchema<any>;

  useEffect(() => {
    pushToDataLayer({
      event: 'embedded_quote_load',
      data: {},
    });
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={ValidationSchema}
      enableReinitialize={true}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        values,
        errors,
        setFieldTouched,
        setFieldValue,
      }: {
        values: any;
        errors: any;
        setFieldTouched: any;
        setFieldValue: any;
      }) => {
        useEffect(() => {
          if (service) {
            setFieldValue('conveyancy-choice', service);
          }
        }, [service]);

        return (
          <QuickQuoteContext.Provider
            value={{ quoteStep, setQuoteStep, service, setService }}
          >
            <section className={'container mx-auto bg-surface/background py-8'}>
              <div className="grid grid-cols-12">
                <div className="col-span-12 sm:col-span-8 sm:col-start-3 xl:col-span-6 xl:col-start-4 gap-x-6">
                  <Form>
                    {title && (
                      <h2 className="text-2xl md:text-5xl text-center pb-8">
                        {title}
                      </h2>
                    )}
                    <ProgressBar
                      totalSteps={
                        service === Service.BUY_AND_SELL
                          ? 10
                          : service === Service.REMORTGAGE
                          ? 5
                          : 6
                      }
                      quoteStep={quoteStep}
                    />
                    <div className="min-h-[18em]">
                      {/* Service  */}
                      <div
                        className={classNames('hidden', {
                          '!block': quoteStep === 0,
                        })}
                      >
                        <QuickQuoteWrapper
                          heading={service_heading}
                          name={'conveyancy-choice'}
                          inView={quoteStep === 0}
                          content={[
                            <QuickQuoteCardBtn
                              service={Service.BUY}
                              option={Service.BUY}
                              name={'conveyancy-choice'}
                              label={buy_field_name}
                              description={buy_field_description}
                              icon={buy_field_icon}
                            />,
                            <QuickQuoteCardBtn
                              service={Service.SELL}
                              option={Service.SELL}
                              name={'conveyancy-choice'}
                              label={sell_field_name}
                              description={sell_field_description}
                              icon={sell_field_icon}
                            />,
                            <QuickQuoteCardBtn
                              service={Service.BUY_AND_SELL}
                              name={'conveyancy-choice'}
                              option={Service.BUY_AND_SELL}
                              label={buy_and_sell_field_name}
                              description={buy_and_sell_field_description}
                              icon={buy_and_sell_field_icon}
                            />,
                            <QuickQuoteCardBtn
                              service={Service.REMORTGAGE}
                              option={Service.REMORTGAGE}
                              name={'conveyancy-choice'}
                              label={remortgage_field_name}
                              description={remortgage_field_description}
                              icon={remortgage_field_icon}
                            />,
                          ]}
                        />
                      </div>
                      <div
                        className={classNames({
                          hidden: service === Service.BUY_AND_SELL,
                        })}
                      >
                        {/* Property Type */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 1,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={type_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            name={'tenure'}
                            inView={
                              quoteStep === 1 &&
                              service !== Service.BUY_AND_SELL
                            }
                            content={[
                              <QuickQuoteCardBtn
                                name={'tenure'}
                                option={'freehold'}
                                label={freehold_field_name}
                                description={freehold_field_description}
                                icon={freehold_field_icon}
                              />,
                              <QuickQuoteCardBtn
                                name={'tenure'}
                                option={'leasehold'}
                                label={leasehold_field_name}
                                description={leasehold_field_description}
                                icon={leasehold_field_icon}
                              />,
                            ]}
                          />
                        </div>

                        {/* Price */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 2,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={price_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            inView={
                              quoteStep === 2 &&
                              service !== Service.BUY_AND_SELL
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'250,000'}
                                is_required
                                input_type={'text'}
                                name={'price'}
                                prefix_value={'£'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (!values['price'] || errors['price']) {
                                    setFieldTouched('price', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                              />,
                            ]}
                          />
                        </div>

                        {/* Postcode */}
                        <div
                          className={classNames('hidden', {
                            '!block':
                              quoteStep === 3 && service !== Service.REMORTGAGE,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={postcode_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            inView={
                              quoteStep === 3 &&
                              service !== Service.REMORTGAGE &&
                              service !== Service.BUY_AND_SELL
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'CF23 4NL'}
                                is_required
                                input_type={'text'}
                                name={'postcode'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (
                                    !values['postcode'] ||
                                    errors['postcode']
                                  ) {
                                    setFieldTouched('postcode', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['postcode']}
                              />,
                            ]}
                          />
                        </div>

                        {/* Remortgage Postcode */}
                        <div
                          className={classNames('hidden', {
                            '!block':
                              quoteStep === 3 && service === Service.REMORTGAGE,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={postcode_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            inView={
                              quoteStep === 3 && service === Service.REMORTGAGE
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'CF23 4NL'}
                                is_required
                                input_type={'text'}
                                name={'remortgagePostcode'}
                              />,
                              <QuickQuoteCTA
                                label={'FINAL STEP'}
                                onClick={() => {
                                  if (
                                    !values['remortgagePostcode'] ||
                                    errors['remortgagePostcode']
                                  ) {
                                    setFieldTouched(
                                      'remortgagePostcode',
                                      true,
                                      true
                                    );
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['remortgagePostcode']}
                              />,
                            ]}
                          />
                        </div>
                      </div>

                      {/* Buying Position */}
                      <div
                        className={classNames('hidden', {
                          '!block':
                            quoteStep === 4 &&
                            (service === Service.BUY ||
                              service === Service.BUY_AND_SELL),
                        })}
                      >
                        <QuickQuoteWrapper
                          heading={'Your buying position'}
                          buy_field_name={buy_field_name}
                          sell_field_name={sell_field_name}
                          buy_and_sell_field_name={buy_and_sell_field_name}
                          remortgage_field_name={remortgage_field_name}
                          columns={1}
                          inView={
                            quoteStep === 4 &&
                            (service === Service.BUY ||
                              service === Service.BUY_AND_SELL)
                          }
                          content={[
                            <QuickQuoteMultiSelectCardBtn
                              name={'isBuyingWithMortgage'}
                              label={im_buying_with_a_mortgage_label}
                              icon={im_buying_with_a_mortgage_icon}
                            />,
                            <QuickQuoteMultiSelectCardBtn
                              name={'isNewBuild'}
                              label={new_build_property_label}
                              icon={new_build_property_icon}
                            />,
                            <QuickQuoteMultiSelectCardBtn
                              name={'isSecondOrInvestmentProperty'}
                              label={
                                this_is_a_second_or_investment_property_label
                              }
                              icon={
                                this_is_a_second_or_investment_property_icon
                              }
                            />,
                            service === Service.BUY && (
                              <QuickQuoteMultiSelectCardBtn
                                name={'isFirstTimeBuyer'}
                                label={im_a_first_time_buyer_label}
                                icon={im_a_first_time_buyer_icon}
                              />
                            ),
                            <QuickQuoteCTA
                              label={
                                service === Service.BUY ? 'FINAL STEP' : 'Next'
                              }
                              onClick={() => {
                                setQuoteStep(quoteStep + 1);
                              }}
                            />,
                          ]}
                        />
                      </div>

                      {/* Selling Position */}
                      <div
                        className={classNames('hidden', {
                          '!block':
                            (quoteStep === 4 && service === Service.SELL) ||
                            (quoteStep === 8 &&
                              service === Service.BUY_AND_SELL),
                        })}
                      >
                        <QuickQuoteWrapper
                          heading={'Your selling position'}
                          buy_field_name={buy_field_name}
                          sell_field_name={sell_field_name}
                          buy_and_sell_field_name={buy_and_sell_field_name}
                          remortgage_field_name={remortgage_field_name}
                          columns={1}
                          inView={
                            (quoteStep === 4 && service === Service.SELL) ||
                            (quoteStep === 8 &&
                              service === Service.BUY_AND_SELL)
                          }
                          content={[
                            <QuickQuoteMultiSelectCardBtn
                              name={'hasMortgageOnProperty'}
                              label={
                                i_already_have_a_mortgage_on_this_property_label
                              }
                              icon={
                                i_already_have_a_mortgage_on_this_property_icon
                              }
                            />,
                            <QuickQuoteCTA
                              label={'FINAL STEP'}
                              onClick={() => {
                                setQuoteStep(quoteStep + 1);
                              }}
                            />,
                          ]}
                        />
                      </div>

                      {/* Buy Property Type */}
                      <div
                        className={classNames({
                          hidden: service !== Service.BUY_AND_SELL,
                        })}
                      >
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 1,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={type_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            isBuying
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 1
                            }
                            content={[
                              <QuickQuoteCardBtn
                                name={'buyTenure'}
                                option={'freehold'}
                                label={freehold_field_name}
                                description={freehold_field_description}
                                icon={freehold_field_icon}
                              />,
                              <QuickQuoteCardBtn
                                name={'buyTenure'}
                                option={'leasehold'}
                                label={leasehold_field_name}
                                description={leasehold_field_description}
                                icon={leasehold_field_icon}
                              />,
                            ]}
                          />
                        </div>

                        {/*  Buy Price */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 2,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={price_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            isBuying
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 2
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'250,000'}
                                is_required
                                input_type={'text'}
                                name={'buyPrice'}
                                prefix_value={'£'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (
                                    !values['buyPrice'] ||
                                    errors['buyPrice']
                                  ) {
                                    setFieldTouched('buyPrice', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['buyPrice']}
                              />,
                            ]}
                          />
                        </div>

                        {/*  Buy Postcode */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 3,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={postcode_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            isBuying
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 3
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'CF23 4NL'}
                                is_required
                                input_type={'text'}
                                name={'buyPostcode'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (
                                    !values['buyPostcode'] ||
                                    errors['buyPostcode']
                                  ) {
                                    setFieldTouched('buyPostcode', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['buyPostcode']}
                              />,
                            ]}
                          />
                        </div>

                        {/* Sell Property Type */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 5,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={type_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            isSelling
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 5
                            }
                            content={[
                              <QuickQuoteCardBtn
                                name={'sellTenure'}
                                option={'freehold'}
                                label={freehold_field_name}
                                description={freehold_field_description}
                                icon={freehold_field_icon}
                              />,
                              <QuickQuoteCardBtn
                                name={'sellTenure'}
                                option={'leasehold'}
                                label={leasehold_field_name}
                                description={leasehold_field_description}
                                icon={leasehold_field_icon}
                              />,
                            ]}
                          />
                        </div>

                        {/*  Sell Price */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 6,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={price_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            isSelling
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 6
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'250,000'}
                                is_required
                                input_type={'text'}
                                name={'sellPrice'}
                                prefix_value={'£'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (
                                    !values['sellPrice'] ||
                                    errors['sellPrice']
                                  ) {
                                    setFieldTouched('sellPrice', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['sellPrice']}
                              />,
                            ]}
                          />
                        </div>

                        {/*  Sell Postcode */}
                        <div
                          className={classNames('hidden', {
                            '!block': quoteStep === 7,
                          })}
                        >
                          <QuickQuoteWrapper
                            heading={postcode_question_heading}
                            buy_field_name={buy_field_name}
                            sell_field_name={sell_field_name}
                            buy_and_sell_field_name={buy_and_sell_field_name}
                            remortgage_field_name={remortgage_field_name}
                            columns={1}
                            isSelling
                            inView={
                              service === Service.BUY_AND_SELL &&
                              quoteStep === 7
                            }
                            content={[
                              <QuickQuoteInput
                                placeholder={'CF23 4NL'}
                                is_required
                                input_type={'text'}
                                name={'sellPostcode'}
                              />,
                              <QuickQuoteCTA
                                label={'Next'}
                                onClick={() => {
                                  if (
                                    !values['sellPostcode'] ||
                                    errors['sellPostcode']
                                  ) {
                                    setFieldTouched('sellPostcode', true, true);
                                  } else {
                                    setQuoteStep(quoteStep + 1);
                                  }
                                }}
                                isDisabled={errors['sellPostcode']}
                              />,
                            ]}
                          />
                        </div>
                      </div>

                      {/* Personal Details */}
                      <div
                        className={classNames('hidden', {
                          '!block':
                            (quoteStep === 5 &&
                              service !== Service.BUY_AND_SELL) ||
                            quoteStep === 9 ||
                            (quoteStep === 4 && service === Service.REMORTGAGE),
                        })}
                      >
                        <QuickQuoteWrapper
                          heading={personal_info_section_heading}
                          buy_field_name={buy_field_name}
                          sell_field_name={sell_field_name}
                          buy_and_sell_field_name={buy_and_sell_field_name}
                          remortgage_field_name={remortgage_field_name}
                          columns={1}
                          inView={
                            (quoteStep === 5 &&
                              service !== Service.BUY_AND_SELL) ||
                            quoteStep === 9 ||
                            (quoteStep === 4 && service === Service.REMORTGAGE)
                          }
                          content={[
                            <QuickQuoteSingleOptionSelect
                              placeholder={title_field_heading}
                              is_required
                              name={'title'}
                              options={[
                                'Mr',
                                'Mrs',
                                'Ms',
                                'Miss',
                                'Mx',
                                'Dr',
                                'Rev',
                                'Prof',
                                'Other',
                              ]}
                            />,
                            <QuickQuoteInput
                              placeholder={first_name_field_heading}
                              is_required
                              input_type={'text'}
                              name={'firstName'}
                            />,
                            <QuickQuoteInput
                              placeholder={last_name_field_heading}
                              is_required
                              input_type={'text'}
                              name={'lastName'}
                            />,
                            <QuickQuoteInput
                              placeholder={email_field_heading}
                              is_required
                              input_type={'text'}
                              name={'email'}
                            />,
                            <QuickQuoteInput
                              placeholder={telephone_field_heading}
                              is_required
                              input_type={'text'}
                              name={'telephone'}
                            />,
                            <QuickQuoteCTA
                              label={get_quote_cta_label}
                              submit
                              onClick={() => {
                                if (
                                  !values['title'] ||
                                  !values['firstName'] ||
                                  !values['lastName'] ||
                                  !values['email'] ||
                                  !values['telephone'] ||
                                  errors['title'] ||
                                  errors['firstName'] ||
                                  errors['lastName'] ||
                                  errors['email'] ||
                                  errors['telephone']
                                ) {
                                  setFieldTouched('title', true, true);
                                  setFieldTouched('firstName', true, true);
                                  setFieldTouched('lastName', true, true);
                                  setFieldTouched('email', true, true);
                                  setFieldTouched('telephone', true, true);

                                  pushToDataLayer({
                                    event: 'quote_step1_get_quote',
                                    data: {
                                      funnel_journey:
                                        getDataLayerQuoteFunnerJourney(
                                          service as ProductType
                                        ),
                                      form_type: 'embedded',
                                      quote_step1_get_quote:
                                        'quote_step1_get_quote',
                                    },
                                  });
                                }
                              }}
                            />,
                          ]}
                        />
                      </div>
                    </div>
                    <div className="pt-8">
                      <QuickQuotePreviousCTA
                        label={
                          quoteStep === 1
                            ? return_to_service_step_cta_label
                            : return_cta_label
                        }
                        onClick={() => setQuoteStep(quoteStep - 1)}
                      />
                    </div>
                  </Form>
                </div>
              </div>
            </section>
            {isSubmitting && (
              <div
                className={`fixed top-6 right-7 cursor-pointer w-6 h-6 z-[60] transition-all duration-500`}
              >
                <div
                  className={`overflow-auto fixed h-screen w-screen z-[59] bg-[#0B212E] bg-opacity-90 top-0 left-0 cursor-pointer py-0 transition-all duration-500 backdrop-blur-sm`}
                >
                  <div className="relative container mx-auto justify-center h-full w-full py-40 grid grid-cols-12 gap-4">
                    <div className="col-span-12 md:col-span-6 md:col-start-4 md:col-end-10 bg-blue-500 text-center items-center flex flex-col justify-center min-h-[200px]">
                      <img src={spinner} alt="loader" className="rotate" />
                      <Heading
                        headingType={HeadingTypes.h3}
                        text="Getting your quote, just a sec..."
                        className="pt-4 !text-[white]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </QuickQuoteContext.Provider>
        );
      }}
    </Formik>
  );
};

export default withBlokWrapper(QuickQuote, Theme.ALT);
